"use client";

import { useEffect, useLayoutEffect, useState } from "react";

/** The page's scrollport — see app/(site)/[locale]/layout.tsx. */
const SCROLLER_ID = "scroll-container";

const useIsomorphicLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * The `container` ref object `useScroll` wants, pointed at #scroll-container.
 *
 * The page does not scroll on `body`, so every scroll-linked section has to
 * hand motion the scrollport by hand. Two things have to be true of that ref
 * at once, which is why it is neither a plain `useRef` nor a plain lookup:
 *
 *  - It has to be filled by the time motion reads it. `useScroll` reads
 *    `.current` in a layout effect on mount and caches the result — a
 *    `useRef`, still empty until commit, is read too late and motion falls
 *    back to tracking the window, which never scrolls here.
 *
 *  - It has to point at the element that is in the document *now*. Switching
 *    language re-fetches the RSC payload for the other `[locale]` tree, and
 *    the segment change remounts the layout that owns the scrollport: the
 *    <div id="scroll-container"> the reader is looking at afterwards is a
 *    brand-new element. A lookup done in the render phase happens *before*
 *    that commit, so it finds the outgoing node and the section spends the
 *    rest of the session listening to a scroller that is no longer in the
 *    document — the card stack never deals, the orbit never turns, and the
 *    section reads as broken until a full page load.
 *
 * So: look it up during the first render, early enough for motion, then
 * re-point it in a layout effect. Layout effects fire in hook-declaration
 * order, so calling this hook *above* `useScroll` means the correction has
 * already landed by the time motion reads the ref.
 */
export function useScrollerRef() {
  const [ref] = useState<React.RefObject<HTMLElement | null>>(() => ({
    current:
      typeof document === "undefined"
        ? null
        : document.getElementById(SCROLLER_ID),
  }));

  useIsomorphicLayoutEffect(() => {
    ref.current = document.getElementById(SCROLLER_ID);
  }, [ref]);

  return ref;
}
